import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, IconButton, Stepper, Step, StepLabel, StepContent,
    Dialog, DialogTitle, DialogContent, DialogActions, Fab, Tooltip, Zoom, Chip, Stack
} from '@mui/material';
import {
    School as TutorialIcon,
    Close as CloseIcon,
    ArrowForward as NextIcon,
    ArrowBack as BackIcon,
    CheckCircle as CompleteIcon,
    PlayArrow as StartIcon,
    CloudUpload as UploadIcon,
    Settings as SettingsIcon,
    Assessment as ReportIcon,
    Psychology as AIIcon
} from '@mui/icons-material';

// Definição dos tutoriais disponíveis
const TUTORIALS = {
    'upload-balancete': {
        id: 'upload-balancete',
        title: 'Upload de Balancete',
        description: 'Aprenda a carregar seus dados de custos contábeis',
        icon: <UploadIcon />,
        duration: '3 min',
        startPage: '/processamento',
        steps: [
            {
                title: 'Acesse a página de Processamento',
                description: 'No menu lateral, clique em "Processamento". Esta é a página onde você carrega e processa seus dados.',
                action: 'navigate',
                target: '/processamento',
                highlight: 'sidebar-processamento'
            },
            {
                title: 'Selecione a Competência',
                description: 'Escolha o mês/ano que deseja processar. Use o seletor de data no topo da página.',
                action: 'observe',
                highlight: 'competencia-selector'
            },
            {
                title: 'Clique em Upload Balancete',
                description: 'Procure pelo botão "Upload Balancete" na seção de uploads. Ele abrirá o seletor de arquivos.',
                action: 'click',
                highlight: 'btn-upload-balancete'
            },
            {
                title: 'Selecione seu arquivo',
                description: 'Escolha um arquivo Excel (.xlsx) ou CSV contendo seu balancete. O arquivo deve ter colunas de conta, centro de custo e valor.',
                action: 'observe'
            },
            {
                title: 'Verifique o mapeamento',
                description: 'O sistema vai mostrar uma prévia dos dados. Confirme se as colunas foram mapeadas corretamente.',
                action: 'observe'
            },
            {
                title: 'Salve os dados',
                description: 'Clique em "Salvar" para importar os dados do balancete. Você verá uma confirmação quando concluído.',
                action: 'click',
                highlight: 'btn-save'
            }
        ]
    },
    'upload-producao': {
        id: 'upload-producao',
        title: 'Upload de Produção',
        description: 'Aprenda a carregar dados de produção e receita',
        icon: <UploadIcon />,
        duration: '3 min',
        startPage: '/processamento',
        steps: [
            {
                title: 'Acesse a página de Processamento',
                description: 'No menu lateral, clique em "Processamento".',
                action: 'navigate',
                target: '/processamento'
            },
            {
                title: 'Clique em Upload Produção',
                description: 'Procure pelo botão "Upload Produção" na seção de uploads.',
                action: 'click'
            },
            {
                title: 'Selecione seu arquivo',
                description: 'Escolha um arquivo Excel ou CSV com dados de produção: procedimentos, quantidades, valores e convênios.',
                action: 'observe'
            },
            {
                title: 'Configure o mapeamento',
                description: 'Mapeie as colunas do seu arquivo para os campos do sistema: código, descrição, centro de custo, quantidade, valor.',
                action: 'observe'
            },
            {
                title: 'Salve os dados',
                description: 'Clique em "Salvar" para importar os dados de produção.',
                action: 'click'
            }
        ]
    },
    'processar-competencia': {
        id: 'processar-competencia',
        title: 'Processar Competência',
        description: 'Aprenda a processar dados e gerar histórico de custos',
        icon: <SettingsIcon />,
        duration: '2 min',
        startPage: '/processamento',
        steps: [
            {
                title: 'Verifique os uploads',
                description: 'Antes de processar, confirme que o balancete e a produção estão carregados. Você verá indicadores verdes.',
                action: 'observe'
            },
            {
                title: 'Selecione a competência',
                description: 'Confirme que a competência (mês/ano) desejada está selecionada.',
                action: 'observe'
            },
            {
                title: 'Clique em Processar',
                description: 'Clique no botão "Processar Competência". O sistema irá cruzar custos e produção.',
                action: 'click'
            },
            {
                title: 'Aguarde o processamento',
                description: 'O sistema pode levar alguns segundos. Você verá uma barra de progresso.',
                action: 'wait'
            },
            {
                title: 'Verifique o resultado',
                description: 'Quando concluído, você verá um resumo com total de custos processados por centro.',
                action: 'observe'
            }
        ]
    },
    'criar-regra-rateio': {
        id: 'criar-regra-rateio',
        title: 'Criar Regra de Rateio',
        description: 'Aprenda a configurar distribuição de custos entre centros',
        icon: <SettingsIcon />,
        duration: '4 min',
        startPage: '/alocacao-custos',
        steps: [
            {
                title: 'Acesse Alocação de Custos',
                description: 'No menu lateral, clique em "Alocação de Custos" ou "Regras de Rateio".',
                action: 'navigate',
                target: '/alocacao-custos'
            },
            {
                title: 'Clique em Nova Regra',
                description: 'Procure pelo botão "Nova Regra" ou "Adicionar" para criar uma nova regra.',
                action: 'click'
            },
            {
                title: 'Escolha o tipo de rateio',
                description: 'Selecione o método:\n• Percentual Fixo: distribui um % fixo\n• Base Produção: proporcional à receita\n• Step-Down: rateio sequencial',
                action: 'select'
            },
            {
                title: 'Configure origem e destino',
                description: 'Selecione o centro de custo de ORIGEM (de onde sai o custo) e os centros de DESTINO (para onde vai).',
                action: 'configure'
            },
            {
                title: 'Defina percentuais ou drivers',
                description: 'Para cada destino, defina o percentual ou o driver de rateio. A soma deve ser 100%.',
                action: 'configure'
            },
            {
                title: 'Salve a regra',
                description: 'Clique em "Salvar" para gravar a regra. Ela será aplicada automaticamente nos relatórios.',
                action: 'click'
            }
        ]
    },
    'gerar-relatorio': {
        id: 'gerar-relatorio',
        title: 'Gerar Relatório de Conferência',
        description: 'Aprenda a gerar e exportar relatórios',
        icon: <ReportIcon />,
        duration: '2 min',
        startPage: '/relatorio-conferencia',
        steps: [
            {
                title: 'Acesse Relatório de Conferência',
                description: 'No menu lateral, em "Relatórios", clique em "Conferência".',
                action: 'navigate',
                target: '/relatorio-conferencia'
            },
            {
                title: 'Selecione a competência',
                description: 'Escolha o mês/ano que deseja visualizar no seletor de competência.',
                action: 'select'
            },
            {
                title: 'Clique em Gerar Relatório',
                description: 'Clique no botão para processar e exibir o relatório.',
                action: 'click'
            },
            {
                title: 'Analise os dados',
                description: 'O relatório mostra custos por centro, receitas, margens e alocações. Use os filtros para explorar.',
                action: 'observe'
            },
            {
                title: 'Exporte se necessário',
                description: 'Clique em "Exportar Excel" para baixar os dados em planilha.',
                action: 'click'
            }
        ]
    },
    'usar-ia': {
        id: 'usar-ia',
        title: 'Usar Análise de IA',
        description: 'Aprenda a explorar insights e previsões automáticas',
        icon: <AIIcon />,
        duration: '3 min',
        startPage: '/ai-analysis',
        steps: [
            {
                title: 'Acesse Análise de IA',
                description: 'No menu lateral, clique em "Análise de IA" ou "Assistente IA".',
                action: 'navigate',
                target: '/ai-analysis'
            },
            {
                title: 'Explore os Insights',
                description: 'Na primeira aba, veja previsões de custos, sazonalidade e destaques estatísticos.',
                action: 'observe'
            },
            {
                title: 'Veja o Dashboard',
                description: 'Na aba "Dashboard", visualize custo total, receita, margem e distribuição por centro.',
                action: 'click'
            },
            {
                title: 'Explore IA Avançada',
                description: 'Na aba "IA Avançada", veja anomalias detectadas, clusters e recomendações.',
                action: 'click'
            },
            {
                title: 'Veja o Plano de Otimização',
                description: 'Na aba "Plano de Otimização", veja planos de ação detalhados com economia potencial.',
                action: 'click'
            },
            {
                title: 'Use o Chat',
                description: 'Na aba "Chat", faça perguntas específicas sobre seus dados. A IA responderá com análises.',
                action: 'interact'
            }
        ]
    }
};

const GuidedTutorial = ({ isOpen, onClose, tutorialId }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const tutorial = TUTORIALS[tutorialId];

    useEffect(() => {
        if (isOpen && tutorial) {
            setActiveStep(0);
            setCompleted(false);
        }
    }, [isOpen, tutorialId]);

    if (!tutorial) return null;

    const handleNext = () => {
        const currentStep = tutorial.steps[activeStep];

        // Se a ação é navegação, navega para a página
        if (currentStep.action === 'navigate' && currentStep.target) {
            navigate(currentStep.target);
        }

        if (activeStep < tutorial.steps.length - 1) {
            setActiveStep(activeStep + 1);
        } else {
            setCompleted(true);
        }
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

    const handleStart = () => {
        if (tutorial.startPage && location.pathname !== tutorial.startPage) {
            navigate(tutorial.startPage);
        }
        setActiveStep(0);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <TutorialIcon />
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{tutorial.title}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {tutorial.duration} • {tutorial.steps.length} passos
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {completed ? (
                    <Box textAlign="center" py={4}>
                        <CompleteIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Tutorial Concluído! 🎉
                        </Typography>
                        <Typography color="text.secondary">
                            Você completou o tutorial "{tutorial.title}".
                            Agora você sabe como usar esta funcionalidade!
                        </Typography>
                    </Box>
                ) : (
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {tutorial.steps.map((step, index) => (
                            <Step key={index}>
                                <StepLabel>
                                    <Typography fontWeight={activeStep === index ? 'bold' : 'normal'}>
                                        {step.title}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'pre-line', mb: 2 }}
                                    >
                                        {step.description}
                                    </Typography>

                                    {step.action && (
                                        <Chip
                                            size="small"
                                            label={
                                                step.action === 'navigate' ? '🔗 Navegação' :
                                                    step.action === 'click' ? '👆 Clique' :
                                                        step.action === 'observe' ? '👀 Observe' :
                                                            step.action === 'configure' ? '⚙️ Configure' :
                                                                step.action === 'select' ? '📋 Selecione' :
                                                                    step.action === 'wait' ? '⏳ Aguarde' :
                                                                        '💡 Ação'
                                            }
                                            variant="outlined"
                                            sx={{ mb: 1 }}
                                        />
                                    )}
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
                {completed ? (
                    <>
                        <Button onClick={() => { setCompleted(false); setActiveStep(0); }}>
                            Refazer Tutorial
                        </Button>
                        <Button variant="contained" onClick={onClose}>
                            Finalizar
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            onClick={handleBack}
                            disabled={activeStep === 0}
                            startIcon={<BackIcon />}
                        >
                            Voltar
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                            Passo {activeStep + 1} de {tutorial.steps.length}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            endIcon={activeStep === tutorial.steps.length - 1 ? <CompleteIcon /> : <NextIcon />}
                        >
                            {activeStep === tutorial.steps.length - 1 ? 'Concluir' : 'Próximo'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

// Componente do menu de tutoriais
const TutorialMenu = ({ isOpen, onClose, onSelectTutorial }) => {
    const tutorialList = Object.values(TUTORIALS);

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <TutorialIcon />
                    <Typography variant="h6" fontWeight="bold">Tutoriais Disponíveis</Typography>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Stack>
                    {tutorialList.map((tutorial) => (
                        <Box
                            key={tutorial.id}
                            onClick={() => onSelectTutorial(tutorial.id)}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                cursor: 'pointer',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                        >
                            <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: 'primary.light',
                                color: 'primary.main',
                                display: 'flex'
                            }}>
                                {tutorial.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography fontWeight="bold">{tutorial.title}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {tutorial.description}
                                </Typography>
                            </Box>
                            <Chip label={tutorial.duration} size="small" variant="outlined" />
                        </Box>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

// Botão flutuante de tutoriais
const TutorialButton = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const [selectedTutorial, setSelectedTutorial] = useState(null);

    const handleSelectTutorial = (tutorialId) => {
        setSelectedTutorial(tutorialId);
        setMenuOpen(false);
        setTutorialOpen(true);
    };

    return (
        <>
            <Tooltip title="Tutoriais Guiados" placement="left">
                <Fab
                    color="secondary"
                    size="medium"
                    onClick={() => setMenuOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 100,
                        right: 24,
                        zIndex: 1299,
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #e080e8 0%, #e54a5e 100%)',
                        }
                    }}
                >
                    <TutorialIcon />
                </Fab>
            </Tooltip>

            <TutorialMenu
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                onSelectTutorial={handleSelectTutorial}
            />

            <GuidedTutorial
                isOpen={tutorialOpen}
                onClose={() => setTutorialOpen(false)}
                tutorialId={selectedTutorial}
            />
        </>
    );
};

export { GuidedTutorial, TutorialMenu, TutorialButton, TUTORIALS };
export default TutorialButton;
